import { Badge, Group, Paper, Select, Stack, Text, Title } from '@mantine/core';
import { IconCards, IconUser } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import type { SpreadPlacedCardView, SpreadSessionView } from '@creativitas/shared';
import { PlayingCard } from '@/components/domain/PlayingCard';
import { CardPreviewModal } from './CardPreviewModal';
import { participantName } from './spread-table';

/**
 * История карт сессии — то же, что «Карты партии» в игре.
 *
 * На показе групповая сессия была одним общим полотном, и возражение Дарьи
 * было прямым: «каждого человека интересуют его карты… чтобы он имел доступ
 * именно к своим». Стол при этом остаётся общим — в группе смотрят и на чужие
 * карты, в этом смысл встречи, — а панель отвечает на вопрос «которые из них
 * мои», крупнее и в том порядке, в каком человек с ними встретился.
 *
 * Отсюда умолчания: участник открывает панель на своих картах, ведущий — на
 * всех. Переключиться может каждый: участнику иногда нужно увидеть весь стол,
 * ведущему — сосредоточиться на одном человеке.
 */

const ALL = 'all';

export function SpreadCardsPanel({
  session,
  viewerParticipantId,
  facilitator = false,
}: {
  session: SpreadSessionView;
  /** Кто смотрит. У ведущего своего места за столом может и не быть. */
  viewerParticipantId?: string;
  facilitator?: boolean;
}) {
  const [preview, setPreview] = useState<SpreadPlacedCardView | null>(null);
  const [owner, setOwner] = useState<string>(
    facilitator || !viewerParticipantId ? ALL : viewerParticipantId,
  );

  const positionById = useMemo(
    () => new Map(session.spread.positions.map((position) => [position.id, position])),
    [session.spread.positions],
  );

  const ownerOptions = useMemo(() => {
    if (!facilitator && viewerParticipantId) {
      return [
        { value: viewerParticipantId, label: 'Мои' },
        { value: ALL, label: 'Все карты сессии' },
      ];
    }
    return [
      { value: ALL, label: 'Все' },
      ...session.participants.map((participant) => ({
        value: participant.id,
        label: participant.displayName,
      })),
    ];
  }, [facilitator, session.participants, viewerParticipantId]);

  /*
    Порядок вытягивания — это запись: `createdAt` говорит, когда карта пришла, и
    панель читается сверху вниз в том порядке, в каком человек её встретил.
  */
  const all = useMemo(
    () => [...session.placedCards].sort((a, b) => a.createdAt.localeCompare(b.createdAt)),
    [session.placedCards],
  );
  const visible = owner === ALL ? all : all.filter((card) => card.placedByParticipantId === owner);
  const filtered = visible.length !== all.length;

  return (
    <>
      <Paper withBorder p="md" data-testid="spread-cards">
        <Group justify="space-between" mb="sm" gap="sm" wrap="wrap">
          <Group gap="sm" wrap="wrap">
            <Group gap="xs">
              <IconCards size={18} color="var(--accent)" />
              <Title order={4}>Карты сессии</Title>
            </Group>
            {/*
              В индивидуальной сессии выбирать не из кого: вопрос «чьи карты»
              имеет один ответ, и переключатель был бы украшением.
            */}
            {session.participants.length > 1 ? (
              <Select
                size="xs"
                w={200}
                data={ownerOptions}
                value={owner}
                onChange={(next) => setOwner(next ?? ALL)}
                allowDeselect={false}
                comboboxProps={{ withinPortal: true }}
                aria-label="Чьи карты показывать"
              />
            ) : null}
          </Group>
          {/* С отбором голое число читалось бы как «это все карты». */}
          <Badge
            variant="light"
            color={visible.length > 0 ? 'teal' : 'gray'}
            data-testid="spread-cards-count"
          >
            {filtered ? `${visible.length} из ${all.length}` : all.length}
          </Badge>
        </Group>

        {all.length === 0 ? (
          <Text size="sm" c="var(--text-muted)">
            Карты ещё не вытягивали.
          </Text>
        ) : visible.length === 0 ? (
          <Text size="sm" c="var(--text-muted)">
            Под этот отбор карт нет.
          </Text>
        ) : (
          <Group gap="md" align="flex-start" wrap="wrap">
            {visible.map((card) => {
              const position = card.positionId ? positionById.get(card.positionId) : undefined;
              const ownerName = card.placedByParticipantId
                ? participantName(session, card.placedByParticipantId)
                : undefined;
              return (
                <Stack key={card.id} gap={6} align="center" style={{ width: 168 }}>
                  <PlayingCard
                    frontImage={card.card.frontImage}
                    backImage={null}
                    faceUp
                    label={position?.title ?? card.deckTitle}
                    width={168}
                    onClick={() => setPreview(card)}
                  />
                  <Text size="sm" fw={600} ta="center" lh={1.25}>
                    {position?.title ?? card.deckTitle}
                  </Text>
                  {/*
                    Имя подписывается, только когда в отборе больше одного
                    человека: в своих картах подпись под каждой — шум.
                  */}
                  {owner === ALL && ownerName ? (
                    <Text size="xs" c="var(--text-subtle)" ta="center">
                      {card.placedByParticipantId === viewerParticipantId ? 'вы' : ownerName}
                    </Text>
                  ) : null}
                  {/* Карта участника, но тянула её рука ведущего — история это помнит. */}
                  {card.drawnByFacilitator ? (
                    <Text size="xs" c="var(--text-subtle)" ta="center" data-testid="drawn-by-facilitator">
                      вытянул ведущий
                    </Text>
                  ) : null}
                  {position?.hint ? (
                    <Text size="xs" c="var(--text-subtle)" ta="center" lh={1.3}>
                      {position.hint}
                    </Text>
                  ) : null}
                </Stack>
              );
            })}
          </Group>
        )}
      </Paper>

      <CardPreviewModal card={preview} onClose={() => setPreview(null)} />
    </>
  );
}

/**
 * Сколько кто разложил — взгляд ведущего на ту же встречу (SPR-15).
 *
 * У каждого участника свой расклад, поэтому «2 из 4» значит ровно то, что
 * написано: этот человек занял две позиции из четырёх.
 */
export function SpreadProgressByParticipant({ session }: { session: SpreadSessionView }) {
  const total = session.spread.positions.length;
  const counts = new Map<string, number>();
  for (const card of session.placedCards) {
    if (!card.placedByParticipantId) continue;
    counts.set(card.placedByParticipantId, (counts.get(card.placedByParticipantId) ?? 0) + 1);
  }
  const guests = session.participants.filter((participant) => participant.kind !== 'facilitator');
  if (guests.length === 0) return null;

  return (
    <Paper withBorder p="md" data-testid="spread-progress">
      <Group gap="xs" mb="sm">
        <IconUser size={18} color="var(--accent)" />
        <Title order={4}>Расклады участников</Title>
      </Group>
      <Stack gap="xs">
        {guests.map((participant) => {
          const opened = counts.get(participant.id) ?? 0;
          return (
            <Group key={participant.id} justify="space-between">
              <Text size="sm">{participant.displayName}</Text>
              <Badge
                variant="light"
                color={total > 0 && opened >= total ? 'teal' : opened > 0 ? 'gold' : 'gray'}
              >
                {total > 0 ? `${opened} из ${total}` : `карт: ${opened}`}
              </Badge>
            </Group>
          );
        })}
      </Stack>
    </Paper>
  );
}
